/* TODO: t() */
/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { Component, createRef, ElementRef } from 'react';
import { View, StyleSheet } from 'react-native';

import { Form, SubmitButton, Button, Text, Link, TextInput } from '../../uiold';
import { themeColors } from '../../uiold/Constants';
import LoadingIndicator from '../../uiold/LoadingIndicator';
import { isValidEmail } from '../../../core/support/isValidEmail';
import { postJSON } from '../../../support/Fetch';

import { Field, VendorSummary } from '../../../types/shared/Vendor';

type Props = {
  accountID: string;
  formID: string;
  onViewLoaded?: () => void;
  onRenewalDataReceived: (renewalData: Array<Field>) => void;
  onClose: () => void;
};

type State = {
  isInputError: boolean;
  emailAddress: string;
  otp: string;
  vendors: Array<VendorSummary>;
  isSubmitting: boolean;
  showStep: 'CONFIRMATION' | 'EMAIL_FORM' | 'OTP_FORM' | 'VENDOR_SELECTION';
  errorMessage: string | null | undefined;
};

type TextInputInstance = ElementRef<typeof TextInput>;

class RenewalView extends Component<Props, State> {
  _inputRef = createRef<TextInputInstance>();
  state: State = {
    isInputError: false,
    emailAddress: '',
    otp: '',
    vendors: [],
    isSubmitting: false,
    showStep: 'CONFIRMATION',
    errorMessage: null,
  };

  componentDidMount() {
    if (this.props.onViewLoaded) {
      this.props.onViewLoaded();
    }
  }

  render() {
    let { showStep, isSubmitting } = this.state;
    if (isSubmitting) {
      return <LoadingIndicator />;
    }
    switch (showStep) {
      case 'CONFIRMATION': {
        return this._renderConfirmation();
      }
      case 'EMAIL_FORM': {
        return this._renderEmailForm();
      }
      case 'OTP_FORM': {
        return this._renderOTPForm();
      }
      case 'VENDOR_SELECTION': {
        return this._renderVendorSelection();
      }
      default: {
        throw new Error('Invalid showStep value.');
      }
    }
  }

  _renderConfirmation() {
    let { onClose } = this.props;
    return (
      <View style={styles.container}>
        <Text style={styles.renewalTitle}>Renewal?</Text>
        <Text type="label">
          If you are a returning member, you can have your information
          pre-filled from your previous application by completing the renewal
          flow which requires verifying your email address.
        </Text>
        <View style={styles.buttonRow}>
          <Button
            textStyle={styles.buttonText}
            title="Renewal"
            style={styles.button}
            hoverStyle={styles.hoveredButton}
            onPress={this._proceed}
          />
          <Button
            textStyle={styles.buttonText}
            title="New Application"
            style={styles.button}
            hoverStyle={styles.hoveredButton}
            onPress={onClose}
          />
        </View>
      </View>
    );
  }

  _renderEmailForm() {
    let { onClose } = this.props;
    let { emailAddress } = this.state;
    return (
      <Form style={styles.container} onSubmit={this._submitRenewalRequest}>
        {this._renderError()}
        <Text style={{ marginVertical: 8 }} type="label">
          Please enter the email address you last used to apply.{' '}
          <strong>
            You may only request a renewal for an application from the previous
            season the market operated.
          </strong>
        </Text>
        <View style={styles.formRow}>
          <TextInput
            autoFocus
            keyboardType="email-address"
            ref={this._inputRef}
            value={emailAddress}
            onChangeText={(emailAddress) => this.setState({ emailAddress })}
            style={styles.textInput}
            placeholder="Your Email"
          />
        </View>
        <View style={styles.buttonRow}>
          <Button
            textStyle={styles.cancelText}
            title="Cancel"
            style={styles.cancelButton}
            hoverStyle={styles.hoveredCancel}
            onPress={onClose}
          />
          <SubmitButton
            textStyle={styles.buttonText}
            title="Continue Renewal"
            style={styles.button}
            hoverStyle={styles.hoveredButton}
          />
        </View>
      </Form>
    );
  }

  _renderOTPForm = () => {
    let { onClose } = this.props;
    let { otp } = this.state;
    return (
      <Form style={styles.container} onSubmit={this._submitOTP}>
        {this._renderError()}
        <Text type="heading">Verification Code</Text>
        <Text style={{ marginVertical: 8 }} type="label">
          A verification code has been sent to your email address. Please enter
          the code below.
        </Text>
        <View style={styles.formRow}>
          <TextInput
            autoFocus
            ref={this._inputRef}
            value={otp}
            onChangeText={(otp) => this.setState({ otp })}
            style={styles.textInput}
            placeholder="Verification Code"
          />
        </View>
        <View style={styles.buttonRow}>
          <Button
            textStyle={styles.cancelText}
            title="Cancel"
            style={styles.cancelButton}
            hoverStyle={styles.hoveredCancel}
            onPress={onClose}
          />
          <SubmitButton
            textStyle={styles.buttonText}
            title="Continue Renewal"
            style={styles.button}
            hoverStyle={styles.hoveredButton}
          />
        </View>
      </Form>
    );
  };

  _renderVendorSelection = () => {
    let { vendors } = this.state;
    return (
      <View>
        <Text type="heading">
          Choose from the list of profiles associated with your email address:
        </Text>
        {vendors.map((vendor: VendorSummary, i: number) => (
          <View key={i} style={{ marginVertical: 12 }}>
            <Link
              onPress={() => {
                this._onVendorSelect(vendor);
              }}
            >
              {vendor.name}
            </Link>
          </View>
        ))}
      </View>
    );
  };

  _renderError = () => {
    let { errorMessage } = this.state;
    if (!errorMessage) {
      return;
    }
    return (
      <View>
        <Text style={styles.errorDisplay} type="heading">
          Error
        </Text>
        <Text style={styles.errorDisplay}>{errorMessage}</Text>
      </View>
    );
  };

  _onEmailInputBlur = () => {
    this.setState({
      isInputError: !this._validateEmailInput(),
    });
  };

  _proceed = () => {
    this.setState({ showStep: 'EMAIL_FORM' });
  };

  _submitRenewalRequest = () => {
    this._onEmailInputBlur();
    let isValid = this._validateEmailInput();
    if (!isValid) {
      this.setState({ isInputError: true }, () => {
        setTimeout(() => {
          let instance = this._inputRef.current;
          instance && instance.focus();
        }, 500);
      });

      return;
    }
    this.setState({ isSubmitting: true, errorMessage: '' });
    let { accountID, formID } = this.props;
    let { emailAddress } = this.state;
    let url = `/api/renewal/${accountID}/${formID}`;
    let data = {
      email: emailAddress,
      url: document.referrer,
    };
    postJSON(url, data, (error, responseData) => {
      let { success, errorMessage } = responseData;
      if (success) {
        this.setState({
          isSubmitting: false,
          showStep: 'OTP_FORM',
        });
      } else {
        this.setState({
          isSubmitting: false,
          errorMessage:
            typeof errorMessage === 'string'
              ? errorMessage
              : 'Unspecified error',
        });
      }
    });
  };

  _submitOTP = () => {
    this.setState({ isSubmitting: true, errorMessage: '' });
    let { accountID } = this.props;
    let { emailAddress, otp } = this.state;
    let url = '/api/renewal/validate';
    let data = {
      account: accountID,
      email: emailAddress,
      code: otp,
    };
    postJSON(url, data, (error, responseData) => {
      let { vendors, errorMessage } = responseData;
      if (vendors && vendors.length) {
        if (vendors.length === 1) {
          this._onVendorSelect(vendors[0]);
        } else {
          this.setState({
            isSubmitting: false,
            showStep: 'VENDOR_SELECTION',
            vendors,
          });
        }
      } else {
        this.setState({
          isSubmitting: false,
          errorMessage:
            typeof errorMessage === 'string'
              ? errorMessage
              : 'Unspecified error',
        });
      }
    });
  };

  _onVendorSelect = (vendor: VendorSummary) => {
    this.setState({ isSubmitting: true, errorMessage: '' });
    let { accountID, onRenewalDataReceived } = this.props;
    let { otp } = this.state;
    let url = '/api/renewal/validate/vendor';
    let data = {
      account: accountID,
      vendor: vendor.id,
      code: otp,
    };
    postJSON(url, data, (error, responseData) => {
      let { success, fields, errorMessage } = responseData;
      if (success && fields) {
        onRenewalDataReceived(fields);
      } else {
        this.setState({
          isSubmitting: false,
          errorMessage:
            typeof errorMessage === 'string'
              ? errorMessage
              : 'Unspecified error',
        });
      }
    });
  };

  _validateEmailInput = () => {
    let { emailAddress } = this.state;
    return isValidEmail(emailAddress);
  };
}

let styles = StyleSheet.create({
  container: {
    marginHorizontal: 20,
  },
  errorDisplay: {
    color: '#900',
    fontWeight: '700',
  },
  formRow: {
    marginVertical: 10,
  },
  buttonRow: {
    flexDirection: 'row',
    marginVertical: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  button: {
    marginHorizontal: 20,
    backgroundColor: '#337ab7',
    borderColor: '#2e6da4',
    borderWidth: 1,
    paddingHorizontal: 0,
    paddingVertical: 0,
  },
  cancelButton: {
    marginHorizontal: 20,
    backgroundColor: themeColors.white,
    borderColor: themeColors.borderGrey,
    borderWidth: 1,
    paddingHorizontal: 0,
    paddingVertical: 0,
  },
  hoveredCancel: {
    backgroundColor: themeColors.lightGrey,
  },
  hoveredButton: {
    backgroundColor: '#286090',
    borderColor: '#204d74',
    borderWidth: 1,
  },
  buttonText: {
    fontSize: 18,
    fontWeight: '400',
    color: themeColors.white,
    paddingVertical: 10,
    paddingHorizontal: 15,
  },
  cancelText: {
    fontSize: 18,
    fontWeight: '400',
    color: themeColors.black,
    paddingVertical: 10,
    paddingHorizontal: 15,
  },
  renewalTitle: {
    fontSize: 36,
    fontWeight: '400',
  },
  textInput: {},
});

export default RenewalView;
